import { useEffect, useState } from "react";
import { NetworkManager } from "./NetworkManager";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { MouseEventHandler } from "react";
import GenericPopup from "./GenericPopup";

interface ImageItem {
  original: string;
  thumbnail?: string;
  caption?: string;
  comment?: string;
}

interface ImageModalProps {
  isOpen: boolean;
  imageSrcs: any[];
  caption?: string;
  onClose: () => void;
  downloadImg?: boolean;
  startIndex?: number;
}


const ImageModal = ({isOpen, imageSrcs, caption = "", onClose, downloadImg = false, startIndex = 0}: ImageModalProps) => {
  const [localImageSrcs, setLocalImageSrcs] = useState<any[]>([]);

  useEffect(() => {
    const fetchImages = async () => {
      if (!isOpen) return;

      const extractFileNameWithoutExtension = (path: string): string => {
        const parts = path.split('/');
        const fileName = parts[parts.length - 1];
        return fileName.replace(/\.[^/.]+$/, '');
      };


      const normalizeImage = (img: any): ImageItem => {
        if (typeof img === "string") {
          return { original: img, thumbnail: img };
        }
        return {
          original: img.original,
          thumbnail: img.original,
          caption: img.key?extractFileNameWithoutExtension(img.key):"",
          comment: img.comment,
        };
      };

      if (downloadImg) {
        const stringUrls = imageSrcs.filter((i): i is string => typeof i === "string");
        const promises = stringUrls.map((img) => {
          const qParam = `objectPath=${img}`;
          return new Promise<string>((resolve) => {
            NetworkManager.sendJsonRequest("api/session/downloadUrl", "", qParam, "GET", true, (data: any) => resolve(data || ""), () => resolve(""));
          });
        });

        const resolved = await Promise.all(promises);
        const downloadedImages = resolved.filter(Boolean).map((src) => ({
          original: src,
          thumbnail: src,
        }));

        setLocalImageSrcs(downloadedImages);
      } else {
        const normalized = imageSrcs?.map(normalizeImage);
        setLocalImageSrcs(normalized);
      }
    };

    fetchImages();
  }, [isOpen, downloadImg, imageSrcs]);


  const handleClose = () => {
    setLocalImageSrcs([]);
    onClose();
  };


  const renderLeftNav = (onClick: MouseEventHandler<HTMLElement>, disabled: boolean) => (
    <button
      className="carousel-control-prev position-absolute top-50 start-0 translate-middle-y z-2"
      type="button"
      onClick={onClick}
      disabled={disabled}
    >
      <span className="bg-dark rounded-circle" style={{ padding: "10px 13px" }}>
        <i className="fa-solid fa-chevron-left text-white" />
      </span>
    </button>
  );

  const renderRightNav = (onClick: MouseEventHandler<HTMLElement>, disabled: boolean) => (
    <button
      className="carousel-control-next position-absolute top-50 end-0 translate-middle-y z-2"
      type="button"
      onClick={onClick}
      disabled={disabled}
    >
      <span className="bg-dark rounded-circle" style={{ padding: "10px 13px" }}>
        <i className="fa-solid fa-chevron-right text-white" />
      </span>
    </button>
  );

  const renderItem = (item: ImageItem) => {
    return (
    <div>
      {item?.caption && <h6 className="text-start ms-1 mt-0 mb-3" style={{ fontWeight: 500, cursor: "default" }}>
        {item.caption}
      </h6>}
      <div style={{ textAlign: "center"}}>
        <img
          src={item.original}
          alt={item.caption}
          className="img-fluid"
          style={{
            margin: "auto",
            display: "block",
            width: "auto",
            maxWidth: "100%",
            minHeight: "400px",
            maxHeight: "80vh",
            objectFit: "contain",
            animationName: "zoom",
            animationDuration: "0.6s",
          }}
        />
        {item?.comment && (
          <p
            className="text-start ms-1 mb-4 mt-3"
            style={{
              fontSize: "14px",
              cursor: "default",
              lineHeight: "1.5",
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {item?.comment}
          </p>
        )} 
      </div>
    </div>
  );
  };

  return (
    <>
      {isOpen && (
        <GenericPopup
          className="img-modal"
          header={caption}
          mode={isOpen}
          onClose={handleClose}
          size="lg"
        >
          {localImageSrcs.length === 0 && <p>Loading image...</p>}

          {localImageSrcs.length > 0 && (
            <ImageGallery
              key={startIndex + '-' + localImageSrcs.length}
              items={localImageSrcs}
              startIndex={startIndex}
              showPlayButton={false}
              showFullscreenButton={false}
              showThumbnails={false}
              showBullets={false}
              showIndex={true}
              renderLeftNav={renderLeftNav}
              renderRightNav={renderRightNav}
              renderItem={renderItem}
            />
          )}
        </GenericPopup>
      )}
    </>
  );
};

export default ImageModal;
